<template>
  <form @submit.prevent="handleSubmit">
    <base-input
      v-if="$currentUserCan($permissions.PERM_VIEW_ANY_ORGANIZATIONS)"
      :label="`${$t('COMMON.ORGANIZATION')} (*)`"
      :placeholder="$t('COMMON.ORGANIZATION')"
    >
      <organization-selector
        :allowNone="true"
        :organization="transfer.organization.id"
        :filterable="true"
        :showAll="false"
        :disabled="!!transfer.id"
        @organizationChanged="onOrganizationChanged"
      />
    </base-input>
    <validation-error :errors="apiValidationErrors.organization" />

    <base-input
      :label="`${$t('EQUIPMENT_TRANSFERS.SOURCE_ESTABLISHMENT')}`"
      :placeholder="$t('COMMON.ESTABLISHMENT')"
      v-if="$currentUserCan($permissions.PERM_VIEW_ANY_ESTABLISHMENTS)"
    >
      <establishment-selector :onlyActive="true"
        :disabled="!!transfer.id"
        :establishment="transfer.sourceEstablishment ? transfer.sourceEstablishment.id : null"
        :filterable="true"
        :showAll="false"
        :filterOrganization="transfer.organization.id"
        @establishmentChanged="onSourceEstablishmentChanged"
      />
    </base-input>
    <validation-error :errors="apiValidationErrors.sourceEstablishment" />

    <base-input
      :label="`${$t('EQUIPMENT_TRANSFERS.DESTINATION_ESTABLISHMENT')}`"
      :placeholder="$t('COMMON.ESTABLISHMENT')"
      v-if="$currentUserCan($permissions.PERM_VIEW_ANY_ESTABLISHMENTS)"
    >
      <establishment-selector :onlyActive="true"
        :disabled="!!transfer.id"
        :establishment="transfer.destinationEstablishment ? transfer.destinationEstablishment.id : null"
        :filterable="true"
        :showAll="false"
        :filterOrganization="transfer.organization.id"
        @establishmentChanged="onDestinationEstablishmentChanged"
      />
    </base-input>
    <validation-error :errors="apiValidationErrors.destinationEstablishment" />

    <base-input
      :label="`${$t('EQUIPMENT_TRANSFERS.TRANSFER_DATE')} (*)`"
      :placeholder="$t('EQUIPMENT_TRANSFERS.TRANSFER_DATE')"
    >
      <flat-picker
        v-model="transfer.transfer_date"
        :config="{
          allowInput: true,
          locale: $flatPickrLocale(),
        }"
        class="form-control datepicker"
        @change="onFormChanged()"
      />
    </base-input>
    <validation-error :errors="apiValidationErrors.transfer_date" />

    <base-input :label="`${$t('EQUIPMENT_TRANSFERS.TRANSFER_TYPE')} (*)`">
      <el-radio-group v-model="transfer.transfer_type" @change="onTransferTypeChanged">
        <el-radio :label="TYPE_TEMPORARY">
          {{ $t("EQUIPMENT_TRANSFERS.TYPE_TEMPORARY") }}
        </el-radio>
        <el-radio :label="TYPE_PERMANENT">
          {{ $t("EQUIPMENT_TRANSFERS.TYPE_PERMANENT") }}
        </el-radio>
      </el-radio-group>
    </base-input>
    <validation-error :errors="apiValidationErrors.transfer_type" />

    <base-input
      v-if="transfer.transfer_type === TYPE_TEMPORARY"
      :label="`${$t('EQUIPMENT_TRANSFERS.RETURN_DATE')} (*)`"
      :placeholder="$t('EQUIPMENT_TRANSFERS.RETURN_DATE')"
    >
      <flat-picker
        v-model="transfer.return_date"
        :config="{
          allowInput: true,
          locale: $flatPickrLocale(),
        }"
        class="form-control datepicker"
        @change="onFormChanged()"
      />
    </base-input>
    <validation-error
      v-if="transfer.transfer_type === TYPE_TEMPORARY"
      :errors="apiValidationErrors.return_date"
    />

    <h2 class="mt-4">{{ $t("COMMON.NOTE") }}</h2>
    <html-editor v-model="transfer.excerpt" @change="onFormChanged()" />
    <validation-error :errors="apiValidationErrors.excerpt" />

    <establishment-inactive-alert :establishment="selectedSourceEstablishment" />
    <establishment-inactive-alert
      :establishment="selectedDestinationEstablishment"
    />

    <div class="text-right mt-4">
      <base-button
        v-if="!isSelectedEstablishmentInactive"
        type="primary"
        native-type="submit"
        :loading="loading"
        :disabled="loading"
      >
        {{ submitLabel || $t("COMMON.SAVE") }}
      </base-button>
    </div>
  </form>
</template>

<script>
import cloneDeep from "lodash/cloneDeep";
import moment from "moment";
import { Radio, RadioGroup } from "element-ui";
import flatPicker from "vue-flatpickr-component";
import "flatpickr/dist/flatpickr.css";
import OrganizationSelector from "@/components/OrganizationSelector.vue";
import EstablishmentSelector from "@/components/EstablishmentSelector.vue";
import EstablishmentInactiveAlert from "@/views/Pages/CafeteriaModule/EstablishmentManagement/partials/EstablishmentInactiveAlert.vue";
import HtmlEditor from "@/components/Inputs/HtmlEditor.vue";
import ValidationError from "@/components/ValidationError.vue";
import {
  EQUIPMENT_TRANSFER_TYPE_TEMPORARY,
  EQUIPMENT_TRANSFER_TYPE_PERMANENT,
} from "@/constants/equipmentTransfers";

export default {
  name: "EquipmentTransferForm",
  components: {
    OrganizationSelector,
    EstablishmentSelector,
    EstablishmentInactiveAlert,
    HtmlEditor,
    ValidationError,
    flatPicker,
    [Radio.name]: Radio,
    [RadioGroup.name]: RadioGroup,
  },
  props: {
    transferModel: {
      type: Object,
      required: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    submitLabel: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      transfer: cloneDeep(this.transferModel),
      apiValidationErrors: {},
      selectedSourceEstablishment: null,
      selectedDestinationEstablishment: null,
      TYPE_TEMPORARY: EQUIPMENT_TRANSFER_TYPE_TEMPORARY,
      TYPE_PERMANENT: EQUIPMENT_TRANSFER_TYPE_PERMANENT,
    };
  },
  computed: {
    isSelectedEstablishmentInactive() {
      return (
        !!(
          this.selectedSourceEstablishment &&
          this.selectedSourceEstablishment.active === false
        ) ||
        !!(
          this.selectedDestinationEstablishment &&
          this.selectedDestinationEstablishment.active === false
        )
      );
    },
  },
  created() {
    this.ensureRelationObjects();
  },
  watch: {
    transferModel: {
      deep: true,
      handler(value) {
        this.transfer = cloneDeep(value);
        this.ensureRelationObjects();
      },
    },
  },
  methods: {
    ensureRelationObjects() {
      this.transfer.organization = this.transfer.organization || {
        type: "organizations",
        id: null,
      };
      this.transfer.sourceEstablishment = this.transfer.sourceEstablishment || {
        type: "establishments",
        id: null,
      };
      this.transfer.destinationEstablishment = this.transfer.destinationEstablishment || {
        type: "establishments",
        id: null,
      };
      if (!this.transfer.transfer_type) {
        this.transfer.transfer_type = EQUIPMENT_TRANSFER_TYPE_PERMANENT;
      }
      if (!this.transfer.id) {
        // Nouveau transfert : toujours la date du jour (évite une date figée
        // au chargement de la page si l'onglet reste ouvert plusieurs jours).
        this.transfer.transfer_date = moment().format("YYYY-MM-DD");
      } else if (!this.transfer.transfer_date) {
        this.transfer.transfer_date = moment().format("YYYY-MM-DD");
      }
      this.transfer = this.$fillUserOrganizationData(this.transfer);
    },
    onOrganizationChanged(organizationId) {
      if (this.transfer.id) return;
      this.transfer.organization.id = organizationId;
      this.transfer.sourceEstablishment.id = null;
      this.transfer.destinationEstablishment.id = null;
      this.onFormChanged();
    },
    onSourceEstablishmentChanged(establishmentId, establishment) {
      this.selectedSourceEstablishment = establishment || null;
      if (this.transfer.id) return;
      this.transfer.sourceEstablishment.id = establishmentId;
      this.onFormChanged();
    },
    onDestinationEstablishmentChanged(establishmentId, establishment) {
      this.selectedDestinationEstablishment = establishment || null;
      if (this.transfer.id) return;
      this.transfer.destinationEstablishment.id = establishmentId;
      this.onFormChanged();
    },
    onTransferTypeChanged() {
      if (this.transfer.transfer_type !== EQUIPMENT_TRANSFER_TYPE_TEMPORARY) {
        this.transfer.return_date = null;
      }
      this.onFormChanged();
    },
    onFormChanged() {
      this.apiValidationErrors = {};
      this.$emit("onFormChanged", this.transfer);
    },
    handleSubmit() {
      const sourceId = this.transfer.sourceEstablishment
        ? this.transfer.sourceEstablishment.id
        : null;
      const destinationId = this.transfer.destinationEstablishment
        ? this.transfer.destinationEstablishment.id
        : null;

      if (sourceId && destinationId && sourceId === destinationId) {
        this.$notify({
          type: "danger",
          message: this.$t("EQUIPMENT_TRANSFERS.SOURCE_DESTINATION_MUST_DIFFER"),
        });
        return;
      }

      if (
        this.transfer.transfer_type === EQUIPMENT_TRANSFER_TYPE_TEMPORARY &&
        !this.transfer.return_date
      ) {
        this.$notify({
          type: "danger",
          message: this.$t("EQUIPMENT_TRANSFERS.RETURN_DATE_REQUIRED"),
        });
        return;
      }

      const payload = cloneDeep(this.transfer);
      this.$emit("submit", payload);
    },
  },
};
</script>
